import moment from "moment-timezone";
import { nanoid } from "nanoid";

import * as teamsApi from "@/apiv2/teams";
import * as api from "@/apiv2/timesheets";
import * as alertActions from "@/redux/alerts/alerts.actions";
import * as customersSelectors from "@/redux/customers/customers.selectors";
import * as selectors from "@/redux/timesheets/individualTimesheets/individualTimesheets.selectors";
import { handleError } from "@/redux/utils/error";
import { STATUS } from "@/utils/constants";
import * as date from "@/utils/date";

import {
  RESET,
  SET_ACTIVE_TIME_ENTRY,
  SET_ACTIVE_USER,
  SET_DATES,
  SET_EXPANDED_DATES,
  SET_FROM_DATE,
  SET_INDIVIDUAL_SUMMARY,
  SET_IS_DELETING_STATUS,
  SET_IS_FETCHING_SUMMARY_STATUS,
  SET_IS_FETCHING_TEAM_MEMBERS_STATUS,
  SET_IS_SUBMITTING_TIMESHEET_TO_ACCOUNTING_STATUS,
  SET_IS_UPDATING_TIME_ENTRY_STATUS,
  SET_MODAL_TYPE,
  SET_SELECTED_TIME_ENTRY_UIDS,
  SET_TEAM_MEMBERS,
  SET_TO_DATE,
} from "./individualTimesheets.actionTypes";

const TIME_ENTRY_STATUS = {
  awaiting_approval: "Awaiting Approval",
  approved: "Approved",
  unapproved: "Unapproved",
};

export const reset = () => (dispatch) => {
  dispatch({
    type: RESET,
  });
};

export const setIsFetchingSummaryStatus = (data) => (dispatch) => {
  dispatch({
    type: SET_IS_FETCHING_SUMMARY_STATUS,
    payload: data,
  });
};

export const setIsUpdatingTimeEntryStatus = (data) => (dispatch) => {
  dispatch({
    type: SET_IS_UPDATING_TIME_ENTRY_STATUS,
    payload: data,
  });
};

export const setIsDeletingStatus = (data) => (dispatch) => {
  dispatch({
    type: SET_IS_DELETING_STATUS,
    payload: data,
  });
};

export const isSubmittingTimesheetToAccountingStatus = (data) => (dispatch) => {
  dispatch({
    type: SET_IS_SUBMITTING_TIMESHEET_TO_ACCOUNTING_STATUS,
    payload: data,
  });
};

export const setIndividualSummary = (data) => (dispatch) => {
  dispatch({
    type: SET_INDIVIDUAL_SUMMARY,
    payload: data,
  });
};

export const setFromDate = (data) => (dispatch) => {
  dispatch({
    type: SET_FROM_DATE,
    payload: data,
  });
};

export const setToDate = (data) => (dispatch) => {
  dispatch({
    type: SET_TO_DATE,
    payload: data,
  });
};

export const setDates = (data) => (dispatch) => {
  dispatch({
    type: SET_DATES,
    payload: data,
  });
};

export const setModalType = (data) => (dispatch) => {
  dispatch({
    type: SET_MODAL_TYPE,
    payload: data,
  });
};

export const setActiveUser = (data) => (dispatch) => {
  dispatch({
    type: SET_ACTIVE_USER,
    payload: data,
  });
};

export const setSelectedTimeEntryUids = (data) => (dispatch) => {
  dispatch({
    type: SET_SELECTED_TIME_ENTRY_UIDS,
    payload: data,
  });
};

export const setIsFetchingTeamMembersStatus = (data) => (dispatch) => {
  dispatch({
    type: SET_IS_FETCHING_TEAM_MEMBERS_STATUS,
    payload: data,
  });
};

export const setTeamMembers = (data) => (dispatch) => {
  dispatch({
    type: SET_TEAM_MEMBERS,
    payload: data,
  });
};

export const setExpandedDates = (data) => (dispatch) => {
  dispatch({
    type: SET_EXPANDED_DATES,
    payload: data,
  });
};

export const setActiveTimeEntry = (data) => (dispatch) => {
  dispatch({
    type: SET_ACTIVE_TIME_ENTRY,
    payload: data,
  });
};

export const fetchTeamMembers = () => async (dispatch) => {
  try {
    dispatch(setIsFetchingTeamMembersStatus(STATUS.PENDING));

    const { data } = await teamsApi.getTeams({
      with_inactive: false,
      include_invites: false,
    })();
    dispatch(setTeamMembers(data));
    dispatch(setIsFetchingTeamMembersStatus(STATUS.FULFILLED));

    return data;
  } catch (error) {
    dispatch(setIsFetchingTeamMembersStatus(STATUS.REJECTED));
    handleError(error, dispatch);
  }
};

export const fetchSummary = () => async (dispatch, getState) => {
  try {
    const state = getState();
    const fromDate = selectors.getFromDate(state);
    const toDate = selectors.getToDate(state);
    const activeUser = selectors.getActiveUser(state);
    const company = customersSelectors.companySelector(state);

    // Note: (This is temporary a condition while waiting for QBO Payroll Integration) Automatically determine if we should use grouped view based on accounting system
    const shouldUseGroupedView =
      company?.accountingName === "QBO" ||
      company?.accountingName === "None" ||
      company.accountingName === "MYOB" ||
      company.accountingName === "Xero";

    dispatch(setIsFetchingSummaryStatus(STATUS.PENDING));
    const { data } = await api.getTimesheetSummary({
      start_date: fromDate,
      end_date: toDate,
      user_id: activeUser?.id ?? undefined,
      timezone: date.getCurrentUserTimezone(),
      ...(shouldUseGroupedView && { is_grouped: 1 }),
    })();

    // Handle different response formats
    const processedTimesheet = shouldUseGroupedView
      ? // For grouped response, keep the object structure but convert date keys
        Object.keys(data[0].timesheet).reduce((acc, dateKey) => {
          const localDate = moment(dateKey, "YYYYMMDDTHH:mm:ssZ").format("YYYY-MM-DD");
          acc[localDate] = data[0].timesheet[dateKey];
          return acc;
        }, {})
      : // For standard response, convert to array format
        Object.keys(data[0].timesheet).map((t) => ({
          ...data[0].timesheet[t],
          date: date.toLocalDateTime({
            date: moment(t, "YYYYMMDDTHH:mm:ssZ").format("YYYY-MM-DDTHH:mm:ssZ"),
            timezone: date.getCurrentUserTimezone(),
            format: "YYYY-MM-DD",
          }),
        }));

    dispatch(
      setIndividualSummary({
        ...data[0],
        timesheet: processedTimesheet,
      })
    );

    dispatch(setIsFetchingSummaryStatus(STATUS.FULFILLED));
  } catch (error) {
    dispatch(setIsFetchingSummaryStatus(STATUS.REJECTED));
    handleError(error, dispatch);
  }
};

export const removeTimeEntries =
  ({ timeEntriesUids }) =>
  async (dispatch, getState) => {
    try {
      const state = getState();
      const summary = selectors.getIndividualSummary(state);
      const company = customersSelectors.companySelector(state);

      const shouldUseGroupedView =
        company?.accountingName === "None" ||
        company.accountingName === "MYOB" ||
        company.accountingName === "Xero";

      // Helper function to calculate total hours from time entries
      const calculateTotalHours = (timeEntries) => {
        if (!timeEntries || timeEntries.length === 0) {
          return "0";
        }
        return timeEntries
          .reduce((total, entry) => total + parseFloat(entry.totalHours || 0), 0)
          .toString();
      };

      dispatch(setIsDeletingStatus(STATUS.PENDING));
      await api.deleteTimeEntry({ uid: timeEntriesUids })();

      if (shouldUseGroupedView) {
        dispatch(
          setIndividualSummary({
            ...summary,
            timesheet: summary.timesheet.map((timesheet) => {
              const updatedWork = timesheet.work
                ? {
                    ...timesheet.work,
                    timeEntries: timesheet.work.timeEntries.filter(
                      (t) => !timeEntriesUids.includes(t.uid)
                    ),
                  }
                : timesheet.work;

              const updatedAttendance = timesheet.attendance
                ? {
                    ...timesheet.attendance,
                    timeEntries: timesheet.attendance.timeEntries.filter(
                      (t) => !timeEntriesUids.includes(t.uid)
                    ),
                  }
                : timesheet.attendance;

              const updatedBreaks = timesheet.breaks
                ? {
                    ...timesheet.breaks,
                    timeEntries: timesheet.breaks.timeEntries.filter(
                      (t) => !timeEntriesUids.includes(t.timeTrackingEvent)
                    ),
                  }
                : timesheet.breaks;

              return {
                ...timesheet,
                work: updatedWork
                  ? {
                      ...updatedWork,
                      totalHours: calculateTotalHours(updatedWork.timeEntries),
                    }
                  : updatedWork,
                attendance: updatedAttendance
                  ? {
                      ...updatedAttendance,
                      totalHours: calculateTotalHours(updatedAttendance.timeEntries),
                    }
                  : updatedAttendance,
                breaks: updatedBreaks
                  ? {
                      ...updatedBreaks,
                      totalHours: calculateTotalHours(updatedBreaks.timeEntries),
                    }
                  : updatedBreaks,
              };
            }),
          })
        );
      } else {
        dispatch(
          setIndividualSummary({
            ...summary,
            timesheet: summary.timesheet.map((timesheet) => {
              const timeEntries = timesheet.timeEntries.filter(
                (t) => !timeEntriesUids.includes(t.uid)
              );

              const approvedTimeEntries = timeEntries.filter(
                (t) => t.approvalStatus === "approved"
              );
              const unApprovedTimeEntries = timeEntries.filter(
                (t) => t.approvalStatus === "unapproved"
              );
              const awaitingApprovalTimeEntries = timeEntries.filter(
                (t) => t.approvalStatus === "awaiting_approval"
              );

              // Calculate new total hours for standard view
              const newTotalHours = calculateTotalHours(timeEntries);

              if (approvedTimeEntries.length === timeEntries.length) {
                return {
                  ...timesheet,
                  status: timeEntries.length > 0 ? "approved" : "",
                  timeEntries,
                  totalHours: newTotalHours,
                };
              }
              if (unApprovedTimeEntries.length === timeEntries.length) {
                return {
                  ...timesheet,
                  status: timeEntries.length > 0 ? "unapproved" : "",
                  timeEntries,
                  totalHours: newTotalHours,
                };
              }
              if (awaitingApprovalTimeEntries.length === timeEntries.length) {
                return {
                  ...timesheet,
                  status: timeEntries.length > 0 ? "awaiting_approval" : "",
                  timeEntries,
                  totalHours: newTotalHours,
                };
              }

              return {
                ...timesheet,
                status: timeEntries.length > 0 ? "multiple_statuses" : "",
                timeEntries,
                totalHours: newTotalHours,
              };
            }),
          })
        );
      }

      dispatch(setIsDeletingStatus(STATUS.FULFILLED));
      dispatch(
        alertActions.createAlertAction(
          nanoid(),
          `Timesheet${timeEntriesUids.length > 1 ? "s" : ""} deleted`,
          true,
          "danger"
        )
      );
    } catch (error) {
      handleError(error, dispatch);
    }
  };

export const bulkChangeStatusOfTimeEntries =
  ({ timeEntriesUids, status, triggerPlace = "" }) =>
  async (dispatch, getState) => {
    const state = getState();
    const summary = selectors.getIndividualSummary(state);
    const company = customersSelectors.companySelector(state);

    try {
      const payload = {
        uid: timeEntriesUids,
        status,
      };

      dispatch(
        setIsUpdatingTimeEntryStatus({
          [triggerPlace === "header" ? "header" : timeEntriesUids[0]]: {
            status,
            loading: STATUS.PENDING,
          },
        })
      );

      await api.patchBulkChangeTimeEntryStatus({ data: payload })();

      dispatch(
        setIsUpdatingTimeEntryStatus({
          [triggerPlace === "header" ? "header" : timeEntriesUids[0]]: {
            status,
            loading: STATUS.FULFILLED,
          },
        })
      );

      const shouldUseGroupedView =
        company?.accountingName === "None" ||
        company.accountingName === "MYOB" ||
        company.accountingName === "Xero";

      if (shouldUseGroupedView) {
        const calculateSectionStatus = (entries) => {
          if (!entries || entries.length === 0) {
            return "";
          }

          const approvedEntries = entries.filter((e) => e.approvalStatus === "approved");
          const unapprovedEntries = entries.filter((e) => e.approvalStatus === "unapproved");
          const awaitingEntries = entries.filter((e) => e.approvalStatus === "awaiting_approval");

          if (approvedEntries.length === entries.length) {
            return "approved";
          }
          if (unapprovedEntries.length === entries.length) {
            return "unapproved";
          }
          if (awaitingEntries.length === entries.length) {
            return "awaiting_approval";
          }
          return "multiple_statuses";
        };

        const updateTimeEntries = (entries) =>
          entries
            ? entries.map((entry) =>
                timeEntriesUids.includes(entry.uid) ? { ...entry, approvalStatus: status } : entry
              )
            : [];

        dispatch(
          setIndividualSummary({
            ...summary,
            timesheet: summary.timesheet.map((timesheet) => {
              const updatedWork = timesheet.work
                ? {
                    ...timesheet.work,
                    timeEntries: updateTimeEntries(timesheet.work.timeEntries),
                  }
                : timesheet.work;

              const updatedAttendance = timesheet.attendance
                ? {
                    ...timesheet.attendance,
                    timeEntries: updateTimeEntries(timesheet.attendance.timeEntries),
                  }
                : timesheet.attendance;

              const updatedBreaks = timesheet.breaks
                ? {
                    ...timesheet.breaks,
                    timeEntries: updateTimeEntries(timesheet.breaks.timeEntries),
                  }
                : timesheet.breaks;

              return {
                ...timesheet,
                work: updatedWork
                  ? {
                      ...updatedWork,
                      status: calculateSectionStatus(updatedWork.timeEntries),
                    }
                  : updatedWork,
                attendance: updatedAttendance
                  ? {
                      ...updatedAttendance,
                      status: calculateSectionStatus(updatedAttendance.timeEntries),
                    }
                  : updatedAttendance,
                breaks: updatedBreaks,
              };
            }),
          })
        );
      } else {
        dispatch(
          setIndividualSummary({
            ...summary,
            timesheet: summary.timesheet.map((timesheet) => {
              const timeEntries = timesheet.timeEntries.map((t) => {
                if (timeEntriesUids.includes(t.uid)) {
                  return {
                    ...t,
                    approvalStatus: status,
                  };
                }

                return t;
              });

              const approvedTimeEntries = timeEntries.filter(
                (t) => t.approvalStatus === "approved"
              );
              const unApprovedTimeEntries = timeEntries.filter(
                (t) => t.approvalStatus === "unapproved"
              );
              const awaitingApprovalTimeEntries = timeEntries.filter(
                (t) => t.approvalStatus === "awaiting_approval"
              );

              if (approvedTimeEntries.length === timeEntries.length) {
                return {
                  ...timesheet,
                  status: timeEntries.length > 0 ? "approved" : "",
                  timeEntries,
                };
              }
              if (unApprovedTimeEntries.length === timeEntries.length) {
                return {
                  ...timesheet,
                  status: timeEntries.length > 0 ? "unapproved" : "",
                  timeEntries,
                };
              }
              if (awaitingApprovalTimeEntries.length === timeEntries.length) {
                return {
                  ...timesheet,
                  status: timeEntries.length > 0 ? "awaiting_approval" : "",
                  timeEntries,
                };
              }

              return {
                ...timesheet,
                status: timeEntries.length > 0 ? "multiple_statuses" : "",
                timeEntries,
              };
            }),
          })
        );
      }

      dispatch(
        alertActions.createAlertAction(
          nanoid(),
          `Timesheets marked as ${TIME_ENTRY_STATUS[status]}`,
          true,
          "success"
        )
      );
    } catch (error) {
      handleError(error, dispatch);
    }
  };

export const setPreviousWeekDates = () => (dispatch, getState) => {
  const state = getState();
  const fromDate = selectors.getFromDate(state);
  const weekStartDay = customersSelectors.getWeekCommencesOn(state);

  const fromDatePreviousWeek = moment(fromDate).subtract(1, "weeks").day(weekStartDay);
  const datesOfPreviousWeek = Array.from({ length: 7 }, (_, i) =>
    fromDatePreviousWeek.clone().add(i, "days").format("YYYY-MM-DD")
  );

  dispatch(setDates(datesOfPreviousWeek));
  dispatch(setFromDate(datesOfPreviousWeek[0]));
  dispatch(setToDate(datesOfPreviousWeek.at(-1)));
};

export const setNextWeekDates = () => (dispatch, getState) => {
  const state = getState();
  const fromDate = selectors.getFromDate(state);
  const weekStartDay = customersSelectors.getWeekCommencesOn(state);

  const fromDateNextWeek = moment(fromDate).add(1, "weeks").day(weekStartDay);
  const datesOfNextWeek = Array.from({ length: 7 }, (_, i) =>
    fromDateNextWeek.clone().add(i, "days").format("YYYY-MM-DD")
  );

  dispatch(setDates(datesOfNextWeek));
  dispatch(setFromDate(datesOfNextWeek[0]));
  dispatch(setToDate(datesOfNextWeek.at(-1)));
};

export const selectTimeEntryUids =
  ({ uid }) =>
  (dispatch, getState) => {
    const state = getState();
    const selectedTimeEntryUids = selectors.getSelectedTimeEntryUids(state);

    if (selectedTimeEntryUids) {
      if (selectedTimeEntryUids.includes(uid)) {
        dispatch(
          setSelectedTimeEntryUids(
            selectedTimeEntryUids.filter((selectedUid) => selectedUid !== uid)
          )
        );
      } else {
        dispatch(setSelectedTimeEntryUids([...selectedTimeEntryUids, uid]));
      }
    } else {
      dispatch(
        setSelectedTimeEntryUids({
          [date]: [uid],
        })
      );
    }
  };

export const exportUserTimeEntriesToAccountingPackage =
  ({ userId, timeEntriesUids }) =>
  async (dispatch) => {
    try {
      const payload = {
        user: userId,
        timeEntries: timeEntriesUids,
      };
      dispatch(isSubmittingTimesheetToAccountingStatus(STATUS.PENDING));
      await api.postExportTimeEntriesTimesheets({ data: payload })();
      dispatch(
        alertActions.createAlertAction(
          nanoid(),
          `Timesheets exported successfully`,
          true,
          "success"
        )
      );
    } catch (error) {
      handleError(error, dispatch);
    } finally {
      dispatch(isSubmittingTimesheetToAccountingStatus(STATUS.FULFILLED));
    }
  };
